import { t } from '@lingui/core/macro';

import type { StudyLanguage } from '@/api/core-api/types.gen';

import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';
import {
  getStudyLanguageLabel,
  STUDY_LANGUAGE_LABELS,
  STUDY_LANGUAGES,
} from '@common/utils';

interface ResponseLanguagesSelectProps {
  /** The project's study language — always allowed, so never listed. */
  studyLanguage: StudyLanguage;
  value: StudyLanguage[];
  onChange: (languages: StudyLanguage[]) => void;
  size?: 'xs' | 'sm' | 'md';
  label?: string;
}

/**
 * Multi-select for the additional languages participants may respond in.
 * The study language is implicitly always allowed (the server normalizes it
 * out of the stored list), so it is excluded from the options.
 */
export function ResponseLanguagesSelect({
  studyLanguage,
  value,
  onChange,
  size,
  label = t`Languages participants can respond in`,
}: ResponseLanguagesSelectProps) {
  const items = STUDY_LANGUAGES.filter((code) => code !== studyLanguage)
    .map((code) => ({
      id: code,
      label: STUDY_LANGUAGE_LABELS[code],
    }))
    .toSorted((a, b) => a.label.localeCompare(b.label));

  return (
    <MultiSelectDropdown<StudyLanguage>
      size={size}
      label={label}
      hint={t`${getStudyLanguageLabel(studyLanguage)} is always included.`}
      placeholder={t`Interview language only`}
      items={items}
      selectedKeys={value.filter((code) => code !== studyLanguage)}
      onSelectionChange={(keys) => onChange([...keys])}
      aria-label={label}
    />
  );
}
